import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Users, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useOpenMatches } from '@/hooks/useOpenMatches';
import { OpenMatchCard } from '@/components/match/OpenMatchCard';
import { Carousel, CarouselContent, CarouselItem, CarouselDots, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface OpenMatchesWidgetProps {
  userLocation?: string;
}

export const OpenMatchesWidget: React.FC<OpenMatchesWidgetProps> = ({ userLocation }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: openMatches, isLoading, refetch } = useOpenMatches(userLocation);
  const [joiningMatch, setJoiningMatch] = useState<string | null>(null);
  const [joiningSlot, setJoiningSlot] = useState<string | null>(null);

  const handleJoinMatch = async (matchId: string, team: 1 | 2, position: 1 | 2) => {
    if (!user) {
      toast.error('Please log in to join matches');
      return;
    }

    // Check if user is already in this match
    const existingMatch = openMatches?.find(match => match.id === matchId);
    if (existingMatch && (
      existingMatch.team1_player1 === user.id ||
      existingMatch.team1_player2 === user.id ||
      existingMatch.team2_player1 === user.id ||
      existingMatch.team2_player2 === user.id
    )) {
      toast.error("You're already in this match. Leave your current position first.");
      return;
    }

    const slotKey = `team${team}_player${position}`;
    setJoiningMatch(matchId);
    setJoiningSlot(slotKey);
    
    try {
      const updates: any = {};
      
      if (team === 1 && position === 1) {
        updates.team1_player1 = user.id;
      } else if (team === 1 && position === 2) {
        updates.team1_player2 = user.id;
      } else if (team === 2 && position === 1) {
        updates.team2_player1 = user.id;
      } else if (team === 2 && position === 2) {
        updates.team2_player2 = user.id;
      }

      const { error } = await supabase
        .from('published_matches')
        .update(updates)
        .eq('id', matchId)
        .eq('is_open', true)
        .eq('status', 'scheduled');

      if (error) throw error;

      toast.success("Successfully joined the match!");
      refetch();
      
    } catch (error: any) {
      console.error('Error joining match:', error);
      toast.error(error.message || "Failed to join match");
    } finally {
      setJoiningMatch(null);
      setJoiningSlot(null);
    }
  };

  const handleLeaveMatch = async (matchId: string, team: 1 | 2, position: 1 | 2) => {
    if (!user) {
      toast.error('Please log in to leave matches');
      return;
    }

    const slotKey = `team${team}_player${position}`;
    setJoiningMatch(matchId);
    setJoiningSlot(slotKey);
    
    try {
      const updates: any = {};
      
      if (team === 1 && position === 1) {
        updates.team1_player1 = null;
      } else if (team === 1 && position === 2) {
        updates.team1_player2 = null;
      } else if (team === 2 && position === 1) {
        updates.team2_player1 = null;
      } else if (team === 2 && position === 2) {
        updates.team2_player2 = null;
      }

      const { error } = await supabase
        .from('published_matches')
        .update(updates)
        .eq('id', matchId)
        .eq('is_open', true)
        .eq('status', 'scheduled');

      if (error) throw error;

      toast.success("Successfully left the match!");
      refetch();
      
    } catch (error: any) {
      console.error('Error leaving match:', error);
      toast.error(error.message || "Failed to leave match");
    } finally {
      setJoiningMatch(null);
      setJoiningSlot(null);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center">
            <Users className="h-4 w-4 mr-2 text-primary" />
            Open Matches
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="animate-pulse bg-muted rounded-lg h-20"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!openMatches || openMatches.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
    >
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center">
              <Users className="h-4 w-4 mr-2 text-primary" />
              Open Matches
            </CardTitle>
            {openMatches.length > 2 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/matches')}
                className="text-primary hover:text-primary/90 text-xs"
              >
                View All <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Carousel className="w-full" opts={{ align: "start", loop: false, dragFree: true }}>
            <CarouselContent className="-ml-2 md:-ml-4">
              {openMatches.map((match) => (
                <CarouselItem key={match.id} className="pl-2 md:pl-4 basis-full md:basis-1/2">
                  <OpenMatchCard 
                    match={match} 
                    onJoinMatch={handleJoinMatch}
                    onLeaveMatch={handleLeaveMatch}
                    currentUserId={user?.id}
                    isJoining={joiningMatch === match.id}
                    joiningSlot={joiningSlot}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
            {openMatches.length > 1 && (
              <>
                <CarouselPrevious className="hidden sm:flex" />
                <CarouselNext className="hidden sm:flex" />
              </>
            )}
            <CarouselDots />
          </Carousel>
        </CardContent>
      </Card>
    </motion.div>
  );
};
